import { nearestSeed } from './edt.js'

const TAU = 2 * Math.PI
/** A corner sharper than this cuts as if this sharp, so a spike's rings never cost more than a few times their depth. */
const MAX_CUT_ANGLE = 2.8

/** Per edge, its arc start and the corner cost through its start vertex: the ring r deep reads `arc − r·cost`. Convex corners cost `2 tan(φ/2)`, concave ones minus their arc. */
export function outlineCorners(edges, stride, polys, insideAt) {
	const count = edges.length / stride
	const arc = new Float64Array(count)
	const cum = new Float64Array(count)
	const cost = new Float64Array(count)
	const phi = new Float64Array(count)
	const side = new Float64Array(count)
	const loopOf = new Int32Array(count)
	const loops = []
	let e = 0
	for (const p of polys) {
		const m = p.length / 2
		const len = i => Math.hypot(edges[(e + i) * stride + 2], edges[(e + i) * stride + 3])
		// Which side the inside lies on: probe off the longest edge's middle.
		let longest = 0
		for (let i = 1; i < m; i++) if (len(i) > len(longest)) longest = i
		const o = (e + longest) * stride
		const l = len(longest) || 1
		const probe = Math.min(1, 0.25 * l)
		const left = insideAt(
			edges[o] + 0.5 * edges[o + 2] - (probe * edges[o + 3]) / l,
			edges[o + 1] + 0.5 * edges[o + 3] + (probe * edges[o + 2]) / l
		)
		const sigma = left ? 1 : -1
		let run = 0
		let total = 0
		for (let i = 0; i < m; i++) {
			const a = (e + ((i + m - 1) % m)) * stride
			const b = (e + i) * stride
			const turn = Math.atan2(
				edges[a + 2] * edges[b + 3] - edges[a + 3] * edges[b + 2],
				edges[a + 2] * edges[b + 2] + edges[a + 3] * edges[b + 3]
			)
			const convex = sigma * turn
			const c = convex > 0 ? 2 * Math.tan(Math.min(convex, MAX_CUT_ANGLE) / 2) : convex
			total += c
			arc[e + i] = run
			cum[e + i] = total
			cost[e + i] = c
			phi[e + i] = turn
			side[e + i] = sigma
			loopOf[e + i] = loops.length
			run += len(i)
		}
		loops.push({ length: run, cost: total })
		e += m
	}
	let main = 0
	for (let i = 1; i < loops.length; i++) if (loops[i].length > loops[main].length) main = i
	return { arc, cum, cost, phi, side, loopOf, loops, perimeter: loops[main]?.length ?? 0 }
}

/**
 * The turn along the ring through (px, py), r deep, whose nearest outline edge is `e`; `next`/`prev` from `edgeLinks`. Round a concave vertex the ring is an arc of radius r, so the turn sweeps it by angle.
 */
export function ringTurn(corners, edges, stride, links, e, px, py, r) {
	const { arc, cum, cost, phi, side, loopOf, loops } = corners
	const o = e * stride
	const len2 = edges[o + 2] * edges[o + 2] + edges[o + 3] * edges[o + 3]
	const t = len2 > 0 ? ((px - edges[o]) * edges[o + 2] + (py - edges[o + 1]) * edges[o + 3]) / len2 : 0
	const loop = loops[loopOf[e]]
	const ring = loop.length - r * loop.cost
	if (!(ring > 1e-9)) return 0
	let a
	const v = t <= 0 ? e : t >= 1 ? links.next[e] : -1
	if (v >= 0 && r > 0 && cost[v] < 0) {
		const pv = links.prev[v] * stride
		const pl = Math.hypot(edges[pv + 2], edges[pv + 3]) || 1
		// Inward normal of the edge before the vertex; the next edge's is it turned by phi.
		const nx = (-side[v] * edges[pv + 3]) / pl
		const ny = (side[v] * edges[pv + 2]) / pl
		const vx = px - edges[v * stride]
		const vy = py - edges[v * stride + 1]
		const f = Math.min(1, Math.max(0, Math.atan2(nx * vy - ny * vx, nx * vx + ny * vy) / phi[v]))
		a = arc[v] - r * (cum[v] - cost[v]) - r * cost[v] * f
	} else a = arc[e] + Math.min(1, Math.max(0, t)) * Math.sqrt(len2) - r * cum[e]
	const turn = a / ring
	return turn - Math.floor(turn)
}

/**
 * Writes each cell's distance to the shape's core — the cells within a texel of its deepest — into a, stage units: rings round the core read as the outline rounded.
 * @param {Float32Array} data RGBA, r = signed distance
 * @param {Int32Array} seeds scratch, ≥ width · height
 * @param {Int32Array} out scratch, ≥ width · height
 * @param {Int32Array} rowCol scratch, ≥ width · height
 */
export function coreChannel(data, width, height, texel, inradius, seeds, out, rowCol) {
	const n = width * height
	const floor = inradius - texel
	for (let c = 0; c < n; c++) seeds[c] = inradius > 0 && data[c * 4] >= floor ? c : -1
	const near = nearestSeed(seeds, width, height, out, rowCol)
	for (let c = 0; c < n; c++) {
		const s = near[c]
		data[c * 4 + 3] =
			s < 0 ? 0 : Math.hypot((c % width) - (s % width), Math.floor(c / width) - Math.floor(s / width)) * texel
	}
}

/** (cos, sin) of a turn into g/b of cell `c`. */
export const writeTurn = (data, c, turn) => {
	data[c * 4 + 1] = Math.cos(TAU * turn)
	data[c * 4 + 2] = Math.sin(TAU * turn)
}

/** Turn in [0, 1) round a `w × h` rect spanning [0, w] × [0, h], clockwise from (0, 0), of the boundary point nearest (px, py). */
export function rectTurn(px, py, w, h) {
	let x = Math.min(Math.max(px, 0), w)
	let y = Math.min(Math.max(py, 0), h)
	if (x > 0 && x < w && y > 0 && y < h) {
		const m = Math.min(x, w - x, y, h - y)
		if (m === y) y = 0
		else if (m === w - x) x = w
		else if (m === h - y) y = h
		else x = 0
	}
	const s = y === 0 ? x : x === w ? w + y : y === h ? 2 * w + h - x : 2 * w + 2 * h - y
	const t = s / (2 * Math.max(w + h, 1e-9))
	return t - Math.floor(t)
}

/** A `w × h` rect's ring turn at (px, py), r deep: the turn round the rect inset by r. */
export const rectRingTurn = (px, py, w, h, r) =>
	rectTurn(px - r, py - r, Math.max(w - 2 * r, 0), Math.max(h - 2 * r, 0))

/** Distance from (px, py) to a `w × h` rect's core: its centre segment along the longer side. */
export const rectCore = (px, py, w, h) =>
	Math.hypot(
		Math.max(Math.abs(px - w / 2) - Math.max(w - h, 0) / 2, 0),
		Math.max(Math.abs(py - h / 2) - Math.max(h - w, 0) / 2, 0)
	)
